import {
  getSubscriptionBookStatus,
  hasBookContentKeyInSession,
  hasEditorPermission,
} from "site/services/auth-session.service";

function endOfLocalCalendarDay(y: number, monthIndex: number, day: number): Date {
  return new Date(y, monthIndex, day, 23, 59, 59, 999);
}

function endOfLocalCalendarDayFromInstant(instant: Date): Date {
  return endOfLocalCalendarDay(instant.getFullYear(), instant.getMonth(), instant.getDate());
}

export function parseSubscriptionUntilDate(raw: string): Date | null {
  const s = raw;
  if (!s) return null;

  const isoDateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (isoDateOnly) {
    const y = Number(isoDateOnly[1]);
    const mo = Number(isoDateOnly[2]) - 1;
    const d = Number(isoDateOnly[3]);
    if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
    return endOfLocalCalendarDay(y, mo, d);
  }

  const dmY = /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+\d{1,2}:\d{2})?$/.exec(s);
  if (dmY) {
    const day = Number(dmY[1]);
    const mo = Number(dmY[2]) - 1;
    const y = Number(dmY[3]);
    if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(day)) return null;
    return endOfLocalCalendarDay(y, mo, day);
  }

  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return null;
  return endOfLocalCalendarDayFromInstant(parsed);
}

export function formatSubscriptionUntilLabel(endOfDay: Date): string {
  const formatted = new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(endOfDay);
  return `до ${formatted}`;
}

export function isLikelyValidSubscriptionEmail(s: string): boolean {
  if (!s) return false

  if (s.length < 5 || s.length > 254 || s.includes("..")) {
    return false;
  }
  
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s);
}

export function verifyAuthCodeErrorMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : "";
  if (raw.includes("401") || raw.includes("invalid code")) {
    return "Неверный или просроченный код. Запросите новый.";
  }
  if (raw.includes("403") || raw.toLowerCase().includes("forbidden")) {
    return "Неверный или просроченный код. Запросите новый.";
  }
  if (raw.includes("verify-code")) {
    return "Не удалось подтвердить почту. Запросите новый код или повторите позже.";
  }
  return "Не удалось подтвердить почту. Проверьте код и подключение к интернету.";
}

export function isBookActiveUntilExpired(bookId: string): boolean {
  const row = getSubscriptionBookStatus(bookId);
  if (!row?.access_active_until) {
    return false;
  }
  const d = parseSubscriptionUntilDate(row.access_active_until);
  if (!d) {
    return true;
  }
  return Date.now() > d.getTime();
}

export function hasActiveBookSubscription(bookId: string): boolean {
  const row = getSubscriptionBookStatus(bookId);
  if (row?.status !== "active") {
    return false;
  }
  return !isBookActiveUntilExpired(bookId);
}

export function isFreeExercise(exercise: { is_free?: unknown } | null | undefined): boolean {
  return exercise?.is_free === true;
}

export function shouldBlockPaidLesson(bookId: string, isFree: boolean): boolean {
  if (isFree) return false;
  if (hasEditorPermission()) return false;
  const bid = `${bookId}`;
  if (!bid) return true;
  if (hasActiveBookSubscription(bid)) return false;
  if (!hasBookContentKeyInSession(bid)) return true;
  if (isBookActiveUntilExpired(bid)) return true;
  return false;
}

export const SUBSCRIPTION_MANAGE_BOOK_ID_KEY = "subscription_manage_book_id";

export type BookListRowKind =
  | { kind: "active"; label: string }
  | { kind: "awaiting_payment" }
  | { kind: "payment_failed" }
  | { kind: "expired" }
  | { kind: "manage" };

function activeLabelForBook(untilRaw?: string): string {
  const raw = untilRaw ?? "";
  if (!raw) {
    return "";
  }
  const d = parseSubscriptionUntilDate(raw);
  return d ? formatSubscriptionUntilLabel(d) : "";
}

export function mergeSubscriptionListRowKind(bookId: string): BookListRowKind {
  const row = getSubscriptionBookStatus(bookId);
  if (!row) {
    return { kind: "manage" };
  }
  switch (row.status) {
    case "active":
      return { kind: "active", label: activeLabelForBook(row.access_active_until) };
    case "awaiting_payment":
      return { kind: "awaiting_payment" };
    case "payment_failed":
      return { kind: "payment_failed" };
    case "expired":
      return { kind: "expired" };
    default:
      return { kind: "manage" };
  }
}
